import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth, isStaffUser } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import Home from "@/pages/Home";
import Immigration from "@/pages/Immigration";
import DocumentsIdentity from "@/pages/DocumentsIdentity";
import ItinTax from "@/pages/ItinTax";
import Screening from "@/pages/Screening";
import NewLife from "@/pages/NewLife";
import Citizenship from "@/pages/Citizenship";
import About from "@/pages/About";
import Resources from "@/pages/Resources";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import StartJourney from "@/pages/StartJourney";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PortalLayout from "@/portal/PortalLayout";
import PortalDashboard from "@/portal/Dashboard";
import PortalCases from "@/portal/MyCase";
import PortalDocuments from "@/portal/Documents";
import PortalMessages from "@/portal/Messages";
import PortalTasks from "@/portal/Tasks";
import PortalAppointments from "@/portal/Appointments";
import PortalDeadlines from "@/portal/Deadlines";
import PortalPayments from "@/portal/Payments";
import PortalServices from "@/portal/ServiceRequests";
import PortalIntake from "@/portal/MyIntake";
import PortalProfile from "@/portal/Profile";
import StaffLayout from "@/staff/StaffLayout";
import StaffDashboard from "@/staff/StaffDashboard";
import StaffCases from "@/staff/StaffCases";
import StaffCaseDetail from "@/staff/StaffCaseDetail";
import StaffClients from "@/staff/StaffClients";
import StaffForms from "@/staff/StaffForms";
import StaffTeam from "@/staff/StaffTeam";

function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-ivory">
      <SiteHeader />
      <main className="flex-1">{children}</main>
      <SiteFooter />
    </div>
  );
}

function ProtectedRoute({ children, staffOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory" data-testid="auth-loading">
        <div className="h-10 w-10 rounded-full border-2 border-teal border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (staffOnly && !isStaffUser(user)) return <Navigate to="/portal" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Check URL fragment for session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
      <Route path="/immigration" element={<PublicLayout><Immigration /></PublicLayout>} />
      <Route path="/documents-identity" element={<PublicLayout><DocumentsIdentity /></PublicLayout>} />
      <Route path="/itin-tax" element={<PublicLayout><ItinTax /></PublicLayout>} />
      <Route path="/screening" element={<PublicLayout><Screening /></PublicLayout>} />
      <Route path="/new-life" element={<PublicLayout><NewLife /></PublicLayout>} />
      <Route path="/citizenship" element={<PublicLayout><Citizenship /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
      <Route path="/resources" element={<PublicLayout><Resources /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
      <Route path="/legal" element={<PublicLayout><Legal /></PublicLayout>} />
      <Route path="/start" element={<PublicLayout><StartJourney /></PublicLayout>} />
      <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />

      <Route path="/portal" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
        <Route index element={<PortalDashboard />} />
        <Route path="intake" element={<PortalIntake />} />
        <Route path="cases" element={<PortalCases />} />
        <Route path="services" element={<PortalServices />} />
        <Route path="documents" element={<PortalDocuments />} />
        <Route path="messages" element={<PortalMessages />} />
        <Route path="tasks" element={<PortalTasks />} />
        <Route path="appointments" element={<PortalAppointments />} />
        <Route path="deadlines" element={<PortalDeadlines />} />
        <Route path="payments" element={<PortalPayments />} />
        <Route path="profile" element={<PortalProfile />} />
      </Route>

      <Route path="/staff" element={<ProtectedRoute staffOnly><StaffLayout /></ProtectedRoute>}>
        <Route index element={<StaffDashboard />} />
        <Route path="cases" element={<StaffCases />} />
        <Route path="cases/:caseId" element={<StaffCaseDetail />} />
        <Route path="clients" element={<StaffClients />} />
        <Route path="forms" element={<StaffForms />} />
        <Route path="team" element={<StaffTeam />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors theme="dark" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
